import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { push } from "store/actions/router";
import { isNil, get } from "lodash";

import PlaidLinkTokenStorage from "../utils/PlaidLinkTokenStorage";
import IconSpinner from "components/IconSpinner";
import {
  linkBankIndividual,
  linkBank,
  oAuthPlaidLinkError,
} from "actions/bankActions";
import { isEmployerSelector } from "store/selectors/user";
import type { RootState } from "types/state";

// Plaid Link is loaded from a global script tag (window.Plaid); it is not
// declared in types/globals.d.ts, so type the slice this component touches
// locally and cast window at the single call site below (mirrors PlaidLink.tsx).
interface PlaidLinkConfig {
  token: string;
  onExit: () => void;
  onSuccess: (token: string, metadata: unknown) => Promise<void>;
  receivedRedirectUri: string;
}
interface PlaidHandler {
  open: () => void;
}

// metadata forwarded to the bank action creators is the raw Plaid Link
// onSuccess payload; the action creators expect PlaidLinkMetadata.
interface PlaidLinkMetadata {
  public_token: string;
  account_id: string;
  link_session_id: string;
  accounts: object[];
  account: object;
  institution: { name?: string; institution_id?: string };
}

const mapStateToProps = (state: RootState) => {
  // profile carries arbitrary backend keys via an index signature, so
  // firstName/lastName are `unknown`; cast at the boundary to interpolate
  // (mirrors PlaidLink.tsx).
  const userLegalName = `${state.user.profile.firstName as string} ${
    state.user.profile.lastName as string
  }`;
  return {
    userId: state.user.id,
    isEmployer: isEmployerSelector(state),
    userLegalName,
  };
};

const mapDispatchToProps = {
  push,
  linkBankIndividual,
  linkBank,
  oAuthPlaidLinkError,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector>>;

class OauthPlaidLink extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  async componentDidMount() {
    const isOAuthRedirect = window.location.href.includes("?oauth_state_id=");

    if (isNil(this.props.userId) || !isOAuthRedirect) {
      this.props.push("/dashboard");
    }

    let tokenContext;
    try {
      // userId is string | null from redux state; getTokenContext expects a
      // string. Pre-existing nullability gap — passed through unchanged.
      tokenContext = PlaidLinkTokenStorage.getTokenContext(
        this.props.userId as string
      );
    } catch (ex) {
      this.props.oAuthPlaidLinkError("Failed to link bank account");
      this.props.push("/dashboard");
    }
    if (isNil(tokenContext) && !isOAuthRedirect) {
      this.props.push("/dashboard");
    } else {
      const previousPath = get(tokenContext, "currentPath") as string;
      var redirectPath = previousPath.includes("link-bank")
        ? "/dashboard"
        : previousPath;
      const token = get(tokenContext, "token") as string;
      const plaidConfig: PlaidLinkConfig = {
        token,
        onExit: () => this.props.push(redirectPath),
        onSuccess: async (token, metadata) =>
          await this._handleOnSuccess({ token, metadata, redirectPath }),
        receivedRedirectUri: window.location.href,
      };

      const plaidLink = (
        window as unknown as {
          Plaid: { create: (config: PlaidLinkConfig) => PlaidHandler };
        }
      ).Plaid.create(plaidConfig);
      plaidLink.open();
    }
  }

  _handleOnSuccess = async ({
    token,
    metadata,
    redirectPath,
  }: {
    token: string;
    metadata: unknown;
    redirectPath: string;
  }) => {
    if (this.props.isEmployer) {
      await this.props.linkBank(
        this.props.client as ApolloClient<object>,
        token,
        metadata as PlaidLinkMetadata
      );
    } else {
      await this.props.linkBankIndividual(
        this.props.client as ApolloClient<object>,
        token,
        metadata as PlaidLinkMetadata,
        this.props.userLegalName
      );
    }
    // userId is string | null from redux state; clearTokenContext expects a
    // string. Pre-existing nullability gap — passed through unchanged.
    PlaidLinkTokenStorage.clearTokenContext(this.props.userId as string);
    this.props.push(redirectPath);
  };

  render() {
    return (
      <div>
        <IconSpinner centered />
      </div>
    );
  }
}

export default connector(withApollo<Props>(OauthPlaidLink));
